import { create } from "zustand";
import axiosInstance from "../axios-interceptor/axios";
import moment from "moment";
const loadUserBooking = () => {
  try {
    const stored = localStorage.getItem("userBooking");
    return stored ? JSON.parse(stored) : null;
  } catch (e) {
    return null;
  }
};
const loadVisitedRoute = () => {
  try {
    const stored = localStorage.getItem("visitedRoutes");
    return stored ? JSON.parse(stored) : null;
  } catch (e) {
    return null;
  }
};
const defaultUserBooking = {
  car: {},
  rate: {},
  booking: {
    pickUpLocation: "East Perth",
    pickUpDate: moment().format("YYYY-MM-DD"),
    pickUpTime: moment().add(1, "hour").startOf("hour").format("hh:mm A"),
    returnLocation: "",
    returnDate: moment().add(7, "days").format("YYYY-MM-DD"),
    returnTime: moment().add(1, "hour").startOf("hour").format("hh:mm A"),
    driverAge: "",
    agreeToTerms: true,
  },
  customer: {
    firstName: "",
    lastName: "",
    email: "",
    contact: "",
    licenseNumber: "",
    licenseExpiryDate: "",
    dateOfBirth: "",
    address: "",
    comments: "",
  },
  extras: [],
};
const mainStore = create((set, get) => ({
  allCars: [],
  cachedCars: [], // Processed cars cache — survives navigation
  cachedCarsTimestamp: 0, // When the cache was last updated
  selectedRoute: "",
  visitedRoutes: loadVisitedRoute() || [],
  currentRoute: "",
  allBookings: [],
  userBooking: loadUserBooking() || defaultUserBooking,
  getCarImg: (car) => {
    try {
      if (car.name === "Suzuki Swift") {
        return "img/cars/suzuki_swift.png";
      } else if (car.name === "Mazda CX3") {
        return "img/cars/mazda_cx3.png";
      } else if (car.name === "Mazda CX8") {
        return "img/cars/mazda_cx8.png";
      } else if (car.name === "KIA Carnival 8 Seater") {
        return "img/cars/KIA Carnival.png";
      } else if (car.name === "Toyota Hilux") {
        return "img/cars/hilux.png";
      } else if (car.name === "Toyota Corolla") {
        return "img/cars/toyota_corrolla.png";
      } else if (car.name === "Outlander 7 Seater") {
        return "img/cars/mitsubishi_out.png";
      } else if (car.name === "Tesla Model 3") {
        return "img/cars/car_6.png";
      }
    } catch (error) {
      return null;
    }
  },
  getAllCars: async (startDate, endDate) => {
    const MAX_RETRIES = 5;
    const RETRY_DELAY = 5000;
    
    // Build query params for date range
    const params = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    
    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        const res = await axiosInstance.get("/cars/public/with-discounts", {
          timeout: 15000,
          params,
        });
        if (res?.data && Array.isArray(res.data) && res.data.length > 0) {
          return res.data;
        }
        console.warn(`getAllCars attempt ${attempt}: empty response, retrying...`);
      } catch (error) {
        console.warn(`getAllCars attempt ${attempt} failed:`, error.message);
      }
      if (attempt < MAX_RETRIES) {
        await new Promise(r => setTimeout(r, RETRY_DELAY));
      }
    }
    console.error('getAllCars: all retries exhausted');
    return [];
  },

  getCarAvailabilityCalendar: async (carId, fromDate, toDate) => {
    try {
      const params = {};
      if (fromDate) params.from = fromDate;
      if (toDate) params.to = toDate;
      const res = await axiosInstance.get(`/cars/${carId}/availability-calendar`, { params });
      return res?.data || null;
    } catch (error) {
      console.error('getCarAvailabilityCalendar error:', error.message);
      return null;
    }
  },

  checkCarAvailability: async (carId, startDate, endDate, excludeSessionId) => {
    try {
      const params = { startDate, endDate };
      if (excludeSessionId) params.excludeSessionId = excludeSessionId;
      const res = await axiosInstance.get(`/cars/${carId}/check-availability`, {
        params,
      });
      return res?.data || null;
    } catch (error) {
      console.error('checkCarAvailability error:', error.message);
      return null;
    }
  },

  // Vehicle Hold System - prevents two customers booking the last unit simultaneously
  createVehicleHold: async (carId, pickUpDate, returnDate, sessionId) => {
    try {
      const res = await axiosInstance.post('/holds', {
        carId,
        pickUpDate,
        returnDate,
        sessionId,
      });
      return res?.data || null;
    } catch (error) {
      console.error('createVehicleHold error:', error.message);
      return null;
    }
  },

  releaseVehicleHold: async (sessionId) => {
    try {
      await axiosInstance.delete(`/holds/${sessionId}`);
    } catch (error) {
      console.error('releaseVehicleHold error:', error.message);
    }
  },

  extendVehicleHold: async (sessionId) => {
    try {
      const res = await axiosInstance.patch(`/holds/${sessionId}/extend`);
      return res?.data || null;
    } catch (error) {
      console.error('extendVehicleHold error:', error.message);
      return null;
    }
  },

  getCarById: async (id) => {
    try {
      const res = await axiosInstance.get(`/cars/${id}`);
      if (res?.data) {
        return res.data;
      }
    } catch (error) {
      return null;
    }
  },

  getAllRates: async () => {
    try {
      const res = await axiosInstance.get("/rates");
      if (res?.data) {
        return res.data;
      }
    } catch (error) {
      return null;
    }
  },

  getAllExtras: async () => {
    try {
      const res = await axiosInstance.get("/extras");
      if (res?.data && Array.isArray(res.data)) {
        return res.data;
      }
      console.warn('getAllExtras: API did not return an array', res?.data);
      return [];
    } catch (error) {
      console.error('getAllExtras error:', error);
      return [];
    }
  },

  // getAllBookings removed — website now relies on API-calculated availability
  // The API's findAllPublicWithDiscounts endpoint handles all availability logic server-side

  createBooking: async (data) => {
    try {
      console.log('🌐 API Call: POST /bookings');
      console.log('📦 Original Request data:', JSON.stringify(data, null, 2));
      
      // Transform the data to match API expectations
      // API expects: car (string ID), rate (string ID), extras (array of string IDs)
      const transformedData = {
        car: data.car._id, // Send as string ID, not object
        rate: data.rate._id, // Send as string ID, not object
        booking: {
          pickUpLocation: data.booking.pickUpLocation,
          pickUpDate: data.booking.pickUpDate,
          pickUpTime: data.booking.pickUpTime,
          returnLocation: data.booking.returnLocation,
          returnDate: data.booking.returnDate,
          returnTime: data.booking.returnTime,
          driverAge: Number(data.booking.driverAge) || 21,
          agreeToTerms: data.booking.agreeToTerms !== false
        },
        customer: {
          firstName: data.customer.firstName,
          lastName: data.customer.lastName,
          email: data.customer.email,
          contact: data.customer.contact, // API expects 'contact', not 'phone'
          licenseNumber: data.customer.licenseNumber || '',
          ...(data.customer.licenseExpiryDate ? { licenseExpiryDate: data.customer.licenseExpiryDate } : {}),
          ...(data.customer.dateOfBirth ? { dateOfBirth: data.customer.dateOfBirth } : {}),
          address: data.customer.address || '',
          comments: data.customer.comments || ''
        },
        extras: (data.extras || []).map(extra => 
          typeof extra === 'string' ? extra : extra._id
        ) // Send as array of string IDs
      };
      
      console.log('📦 Transformed Request data:', JSON.stringify(transformedData, null, 2));
      
      const res = await axiosInstance.post("/bookings", transformedData, {
        timeout: 60000, // 60s for booking — backend sends email before responding
      });
      
      console.log('✅ API Response Status:', res.status);
      console.log('✅ API Response Data:', res.data);
      
      if (res?.data) {
        return res.data;
      }
      console.error('⚠️ Response data was empty, returning null');
      return null;
    } catch (error) {
      console.error('❌ API Error creating booking:', error);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
      console.error('❌ Error message:', error.message);
      
      // Handle 409 Conflict — vehicle was just booked by someone else
      if (error.response?.status === 409) {
        const conflictMsg = error.response?.data?.message || 
          'Sorry, this vehicle was just booked by someone else for these dates. Please choose different dates or another vehicle.';
        alert('⚠️ Booking Conflict\n\n' + conflictMsg);
        return { _conflict: true };
      }
      
      // Show detailed validation errors if available
      if (error.response?.data?.message) {
        console.error('❌ Validation Error:', error.response.data.message);
        
        // Format error message for user
        let errorMsg = 'Booking failed. ';
        if (Array.isArray(error.response.data.message)) {
          errorMsg += error.response.data.message.join(', ');
        } else {
          errorMsg += error.response.data.message;
        }
        alert(errorMsg + '\n\nPlease check your information and try again.');
      } else if (error.response?.status === 400) {
        alert('Booking validation failed. Please check all required fields are filled correctly.');
      } else if (error.response?.status >= 500) {
        alert('Server error. Please try again later or contact support.');
      } else if (error.code === 'ECONNABORTED') {
        // Timeout — booking likely saved, email just took too long
        console.warn('⚠️ Request timed out but booking may have been created');
        return { _timedOut: true };
      } else {
        alert('Booking failed. Please try again or contact support.');
      }
      
      if (error.response?.data?.errors) {
        console.error('❌ Field Errors:', error.response.data.errors);
      }
      
      return null;
    }
  },

  setSelectedRoute: (route) => {
    set({ selectedRoute: route });
  },

  setUserBooking: (booking) => {
    set({ userBooking: booking });
    // Save to localStorage
    localStorage.setItem("userBooking", JSON.stringify(booking));
  },

  setVisitedRoutes: (routes) => {
    set({ visitedRoutes: routes });
    localStorage.setItem("visitedRoutes", JSON.stringify(routes));
  },
  setCurrentRoute: (routes) => {
    set({ currentRoute: routes });
  },

  setCachedCars: (cars) => {
    set({ cachedCars: cars, cachedCarsTimestamp: Date.now() });
  },
}));

export default mainStore;
